<template>
  <transition name="modal-fade">
    <div v-if="isVisible" class="modal-overlay" @click.self="close">
      <div class="modal-container">
        <button class="modal-close" @click="close">&times;</button>

        <div class="modal-header">
          <h3>{{ product.name }}</h3>
          <p class="modal-price">NT$ {{ formatPrice(product.price) }}</p>
        </div>

        <div class="modal-body">
          <!-- Color Selection -->
          <div class="selector-group">
            <label class="selector-label">顏色 *</label>
            <div class="color-options">
              <button
                v-for="color in availableColors"
                :key="color"
                class="color-btn"
                :class="{ active: selectedColor === color }"
                @click="selectColor(color)"
              >
                {{ color }}
              </button>
            </div>
          </div>

          <!-- Size Selection -->
          <div class="selector-group">
            <label class="selector-label">尺寸 *</label>
            <div class="size-options">
              <button
                v-for="size in allPossibleSizes"
                :key="size"
                class="size-btn"
                :class="{
                  active: selectedSize === size,
                  disabled: !isSizeAvailable(size)
                }"
                :disabled="!isSizeAvailable(size)"
                @click="selectSize(size)"
              >
                {{ size }}
              </button>
            </div>
          </div>

          <!-- Stock Info -->
          <div v-if="selectedVariant" class="stock-info">
            <!-- 移除庫存數量顯示，僅保留售完提示 -->
            <span v-if="selectedVariant.inventory === 0" class="stock-out">此規格已售完</span>
          </div>
        </div>

        <div class="modal-footer">
          <button
            class="btn-add-cart"
            :disabled="!selectedVariant || selectedVariant.inventory === 0"
            @click="addToCart"
          >
            加入購物車
          </button>
        </div>
      </div>
    </div>
  </transition>
</template>

<script>
export default {
  props: {
    isVisible: {
      type: Boolean,
      required: true
    },
    product: {
      type: Object,
      required: true
    }
  },
  data () {
    return {
      selectedColor: null,
      selectedSize: null
    }
  },
  computed: {
    variants () {
      return this.product.product_variants || []
    },
    availableColors () {
      const colors = [...new Set(this.variants.map(v => v.color))]
      return colors.sort()
    },
    allPossibleSizes () {
      const sizes = [...new Set(this.variants.map(v => v.size))]
      return sizes.sort((a, b) => parseInt(a, 10) - parseInt(b, 10))
    },
    selectedVariant () {
      if (!this.selectedColor || !this.selectedSize) return null
      return this.variants.find(v => v.color === this.selectedColor && v.size === this.selectedSize)
    }
  },
  watch: {
    isVisible (newVal) {
      if (newVal) {
        // Reset selections when modal opens
        this.initializeSelections()
      }
    }
  },
  methods: {
    initializeSelections () {
      // Auto-select first available variant
      const firstAvailable = this.variants.find(v => v.inventory > 0)
      if (firstAvailable) {
        this.selectedColor = firstAvailable.color
        this.selectedSize = firstAvailable.size
      } else {
        this.selectedColor = this.availableColors[0] || null
        this.selectedSize = null
      }
    },
    selectColor (color) {
      this.selectedColor = color
      // Reset size and try to auto-select first available size for new color
      this.selectedSize = null
      const firstAvailableSize = this.allPossibleSizes.find(size => this.isSizeAvailable(size))
      if (firstAvailableSize) {
        this.selectedSize = firstAvailableSize
      }
    },
    selectSize (size) {
      if (this.isSizeAvailable(size)) {
        this.selectedSize = size
      }
    },
    isSizeAvailable (size) {
      if (!this.selectedColor) return false
      const variant = this.variants.find(v => v.color === this.selectedColor && v.size === size)
      return variant && variant.inventory > 0
    },
    addToCart () {
      if (!this.selectedVariant || this.selectedVariant.inventory === 0) {
        return
      }

      const itemToAdd = {
        id: this.selectedVariant.id,
        productId: this.product.id,
        name: this.product.name,
        price: this.product.price,
        image: this.product.image,
        color: this.selectedVariant.color,
        size: this.selectedVariant.size,
        sku: this.selectedVariant.sku,
        quantity: 1,
        inventory: this.selectedVariant.inventory
      }

      this.$emit('add-to-cart', itemToAdd)
      this.close()
    },
    close () {
      this.$emit('close')
    },
    formatPrice (price) {
      if (price === undefined || price === null) return '0'
      return Number(price).toLocaleString('zh-TW')
    }
  }
}
</script>

<style scoped>
.modal-fade-enter-active, .modal-fade-leave-active {
  transition: opacity 0.3s ease;
}
.modal-fade-enter, .modal-fade-leave-to {
  opacity: 0;
}

.modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background-color: rgba(0, 0, 0, 0.7);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 9999;
  padding: 1rem;
}

.modal-container {
  background-color: var(--pd-white);
  max-width: 500px;
  width: 100%;
  border-radius: 0;
  position: relative;
  max-height: 90vh;
  overflow-y: auto;
}

.modal-close {
  position: absolute;
  top: 1rem;
  right: 1rem;
  background: none;
  border: none;
  font-size: 2rem;
  cursor: pointer;
  color: var(--pd-black);
  z-index: 10;
}

.modal-close:hover {
  opacity: 0.7;
}

.modal-header {
  padding: 2rem;
  border-bottom: 1px solid #e0e0e0;
}

.modal-header h3 {
  margin: 0 0 0.5rem;
  font-size: 1.5rem;
  font-weight: 600;
}

.modal-price {
  margin: 0;
  font-size: 1.25rem;
  font-weight: 500;
}

.modal-body {
  padding: 2rem;
}

.selector-group {
  margin-bottom: 2rem;
}

.selector-label {
  display: block;
  font-weight: 600;
  margin-bottom: 0.75rem;
  font-size: 0.875rem;
  text-transform: uppercase;
  letter-spacing: 0.05em;
}

.color-options,
.size-options {
  display: flex;
  gap: 0.75rem;
  flex-wrap: wrap;
}

.color-btn,
.size-btn {
  padding: 0.75rem 1.5rem;
  border: 1px solid #ccc;
  background-color: var(--pd-white);
  color: var(--pd-black);
  font-size: 0.875rem;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s ease;
  min-width: 50px;
  text-align: center;
}

.color-btn:hover,
.size-btn:hover:not(:disabled) {
  border-color: var(--pd-black);
}

.color-btn.active,
.size-btn.active {
  background-color: var(--pd-black);
  color: var(--pd-white);
  border-color: var(--pd-black);
}

.size-btn.disabled {
  opacity: 0.4;
  cursor: not-allowed;
  background-color: #f9f9f9;
  text-decoration: line-through;
}

.stock-info {
  font-size: 0.875rem;
  color: #ff6b6b;
  font-weight: 600;
  min-height: 1.2em;
}

.stock-out {
  color: #f44336;
}

.modal-footer {
  padding: 2rem;
  border-top: 1px solid #e0e0e0;
}

.btn-add-cart {
  width: 100%;
  padding: 1rem;
  background-color: var(--pd-black);
  color: var(--pd-white);
  border: none;
  font-size: 1rem;
  font-weight: 700;
  text-transform: uppercase;
  cursor: pointer;
  transition: opacity 0.2s ease;
}

.btn-add-cart:hover:not(:disabled) {
  opacity: 0.8;
}

.btn-add-cart:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

/* Mobile Responsive */
@media (max-width: 768px) {
  .modal-container {
    max-width: 100%;
    max-height: 100vh;
    border-radius: 0;
  }

  .modal-header,
  .modal-body,
  .modal-footer {
    padding: 1.5rem;
  }

  .modal-header h3 {
    font-size: 1.25rem;
  }

  .modal-price {
    font-size: 1.125rem;
  }

  .selector-group {
    margin-bottom: 1.5rem;
  }

  .color-btn,
  .size-btn {
    padding: 0.625rem 1.25rem;
    font-size: 0.8125rem;
  }
}

@media (max-width: 480px) {
  .modal-overlay {
    padding: 0;
  }

  .modal-header,
  .modal-body,
  .modal-footer {
    padding: 1rem;
  }

  .color-options,
  .size-options {
    gap: 0.5rem;
  }

  .color-btn,
  .size-btn {
    padding: 0.5rem 1rem;
    min-width: 45px;
  }
}
</style>
